import { useState } from 'react';
import moment from 'moment';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const arrow = 'flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300 dark:hover:bg-gray-600';

// One control for month and year (replaces two dropdowns). Future months are
// disabled; the earliest year matches the old year list (current - 2).
export default function MonthPicker({ year, month, onChange, minYear, now }) {
  const clock = now || moment();
  const maxYear = clock.year();
  const maxMonth = clock.month() + 1;
  const floor = minYear ?? maxYear - 2;
  const [viewYear, setViewYear] = useState(year);

  return (
    <Popover className="relative">
      <PopoverButton
        onClick={() => setViewYear(year)}
        className="inline-flex h-8 items-center gap-2 rounded-md border border-gray-200/70 bg-surface px-2.5 text-xs font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-200"
      >
        {SHORT[month - 1]} {year}
        <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
      </PopoverButton>
      <PopoverPanel className="absolute right-0 z-30 mt-1 w-64 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5 dark:bg-gray-700 dark:ring-white/20">
        {({ close }) => (
          <>
            <div className="mb-2 flex items-center justify-between">
              <button type="button" aria-label="Previous year" disabled={viewYear <= floor} onClick={() => setViewYear(viewYear - 1)} className={arrow}><ChevronLeftIcon className="h-4 w-4" aria-hidden="true" /></button>
              <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{viewYear}</span>
              <button type="button" aria-label="Next year" disabled={viewYear >= maxYear} onClick={() => setViewYear(viewYear + 1)} className={arrow}><ChevronRightIcon className="h-4 w-4" aria-hidden="true" /></button>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {SHORT.map((label, i) => {
                const m = i + 1;
                const future = viewYear === maxYear && m > maxMonth;
                const on = viewYear === year && m === month;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={future}
                    aria-pressed={on}
                    onClick={() => { onChange({ year: viewYear, month: m }); close(); }}
                    className={`h-8 rounded-md text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:text-gray-300 dark:disabled:text-gray-600 ${
                      on ? 'border border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-600 dark:bg-brand-900/30 dark:text-brand-300' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
